<template>
  <template v-if="separated">
    <LcSeparator class="my-200" />

    <header class="pb-200">
      <LcLotteryRemainingPicks
        :complete="remaining <= 0"
        :done-label="$t('powerballxsandos.remainingnumbers.done')"
        v-slot="{ countClass }"
      >
        <i18n-t
          keypath="powerballxsandos.remainingnumbers"
          :plural="visualRemaining"
          scope="global"
        >
          <template #remaining>
            <span :class="countClass">{{ visualRemaining }}</span>
          </template>
        </i18n-t>
      </LcLotteryRemainingPicks>
    </header>
  </template>

  <LcLotteryNumberSelector
    class="number-lottery-xs-and-os-picker-field justify-center"
    v-bind="{ selectableMax, selectableNumbers, value }"
    :highlighted-numbers="getRollingValues(play.id, name)"
    :label="$t('powerballxsandos.playslip.selector.legend')"
    :id="selectorId"
    variant="custom-cell"
    @complete="emit('complete')"
    @update:value="handleUpdate"
  >
    <template #cell="{ checked, disabled, number }">
      <LcLotteryTeamTile
        v-bind="{ disabled, licensedTerritory, logoVariant, lotteryId }"
        :selected="checked"
        :team="decodeXsAndOsTeamName(number)"
      />
    </template>
  </LcLotteryNumberSelector>
</template>

<script lang="ts" setup>
import { decodeXsAndOsTeamName, LcLotteryTeamTile } from '@lottocom/frontend-lotteries'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { EditorPlay } from '../stores/numberLottery'

import { useNumberLotteryStore } from '../stores/numberLottery'
import { findFieldIndexByName } from '../utils/editorFields'

type Emits = {
  (e: 'complete'): void
}

type Props = {
  lotteryId: LotteryId
  name: string
  play: EditorPlay
  selectableMax: number
  selectableNumbers: number[]
  separated?: boolean
}

const emit = defineEmits<Emits>()
const { lotteryId, name, play, selectableMax } = defineProps<Props>()

const store = useNumberLotteryStore(lotteryId)

const licensedTerritory = useLicensedTerritory({ raw: true })
const logoVariant = useXsAndOsLogoVariant()
const { getDisplayPlay, getRollingValues, isAnimating } = useEditorQuickPickAnimation()

const displayPlay = computed(() => getDisplayPlay(play))

const fieldIndex = computed(() => findFieldIndexByName(play, name))

const remaining = computed(() =>
  fieldIndex.value < 0 ? 0 : store.remainingInField(play, fieldIndex.value, selectableMax),
)

const visualRemaining = useEditorVisualRemaining(() => remaining.value)

const selectorId = computed(() => `${play.id}-${name.toLowerCase()}`)

const value = computed(() =>
  fieldIndex.value < 0 ? [] : displayPlay.value.fields[fieldIndex.value].selected,
)

const handleUpdate = (numbers: number[]) => {
  if (fieldIndex.value < 0 || isAnimating(play.id)) return
  store.setSelectedNumbers(play.id, fieldIndex.value, numbers)
}
</script>

<style lang="scss" scoped>
.number-lottery-xs-and-os-picker-field {
  $tile-width: token('size/850');
  $tile-gap: token('size/100');
  $max-columns: 8;

  grid-template-columns: repeat(auto-fit, $tile-width);
  gap: $tile-gap;
  max-width: $max-columns * $tile-width + ($max-columns - 1) * $tile-gap;
  margin-inline: auto;
}

/* stylelint-disable selector-class-pattern */
:deep(.lc-lottery-number-selector__number--highlighted .lc-lottery-team-tile) {
  background-color: token('color/background/accent/info/info');
}
/* stylelint-enable selector-class-pattern */
</style>
